import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Screen from '../../demo/Screen';
import { BuildingGlyph, DocGlyph, FlaskGlyph, GlobeGlyph, InstitutionGlyph } from '../../mocks/TileGlyphs';
import { AgilusMark, ClientLogo, CLIENT_LOGOS, MaxLabMark, SpadiaMark } from './ClientMarks';
import { TestimonialArt } from './ClientArt';
import './Clients.css';

const STATS = [
  { key: 'clients', Glyph: BuildingGlyph, tone: 'blue', value: '40+', label: 'clients' },
  { key: 'reports', Glyph: DocGlyph, tone: 'green', value: '10M+', label: 'smart reports delivered' },
  {
    key: 'biomarkers',
    Glyph: FlaskGlyph,
    tone: 'amber',
    value: '1,500+',
    label: (
      <>
        biomarkers covered
        <br /> (and growing)
      </>
    ),
  },
  { key: 'languages', Glyph: GlobeGlyph, tone: 'purple', value: '4', label: 'languages and counting' },
];

const TESTIMONIALS = [
  {
    key: 'agilus',
    Mark: AgilusMark,
    Avatar: InstitutionGlyph,
    art: 'tower',
    tone: 'blue',
    quote:
      'NirogGyan has transformed our reports into a more meaningful patient experience. Our patients understand their health better and keep coming back.',
    role: 'CEO',
    org: ['Leading Diagnostic Chain,', 'India'],
  },
  {
    key: 'maxlab',
    Mark: MaxLabMark,
    Avatar: BuildingGlyph,
    art: 'glass',
    tone: 'teal',
    quote:
      'The Smart Reports are clear, comprehensive and greatly appreciated by our patients. It has elevated our brand and patient engagement.',
    role: 'Director',
    org: ['Multi-speciality Lab,', 'India'],
  },
  {
    key: 'spadia',
    Mark: SpadiaMark,
    Avatar: BuildingGlyph,
    art: 'city',
    tone: 'amber',
    quote:
      'Implementation was smooth and the team has been highly supportive. Our patients love the easy-to-understand reports and it has improved our review ratings.',
    role: 'CEO',
    org: ['Healthcare Provider,', 'Czech Republic'],
  },
];

const PER_PAGE = 4;
const TILES = CLIENT_LOGOS.length + 1; // +1 for the "And 25+ more…" tile
/* On the fixed 1440 px canvas (1024 px windows and up) every logo fits on one row, as in the mockup: one page. */
const ALL_FIT_QUERY = '(min-width: 1024px)';

function matchesAllFit() {
  return typeof window.matchMedia === 'function' && Boolean(window.matchMedia(ALL_FIT_QUERY)?.matches);
}

function useAllFit() {
  const [fit, setFit] = useState(matchesAllFit);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(ALL_FIT_QUERY);
    if (!mql?.addEventListener) return undefined;
    const onChange = () => setFit(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return fit;
}

function LogoCarousel() {
  const allFit = useAllFit();
  const perPage = allFit ? TILES : PER_PAGE;
  const pages = Math.ceil(TILES / perPage);
  const [chosen, setPage] = useState(1);
  const page = Math.min(chosen, pages);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const start = (page - 1) * perPage;
  const logos = CLIENT_LOGOS.slice(start, start + perPage);
  const last = page === pages;

  const moveFocus = useRef(null);

  // When the pressed arrow becomes disabled, hand focus to the other arrow
  // (after render, once that one is enabled again).
  useEffect(() => {
    moveFocus.current?.current?.focus();
    moveFocus.current = null;
  }, [page]);

  function go(to) {
    if (to === pages) moveFocus.current = prevRef;
    if (to === 1) moveFocus.current = nextRef;
    setPage(to);
  }

  return (
    <section
      className={`card clients-logos${allFit ? ' clients-logos--all' : ''}`}
      aria-labelledby="clients-logos-title"
    >
      <h2 id="clients-logos-title" className="clients-logos__title">
        Some of our clients
      </h2>
      <div className="clients-logos__row">
        <button
          ref={prevRef}
          type="button"
          className="clients-logos__arrow"
          aria-label="Previous clients"
          aria-controls="clients-logos-window"
          disabled={page === 1}
          onClick={() => go(page - 1)}
          data-trapezium-id="packages.clients.logos-prev"
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <div
          id="clients-logos-window"
          className="clients-logos__window"
          data-trapezium-state={`page-${page}`}
        >
          <ul className="clients-logos__list">
            {logos.map((logo) => (
              <li key={logo.key} className={`clients-logo clients-logo--${logo.key}`}>
                <ClientLogo logo={logo} />
              </li>
            ))}
            {last && (
              <li className="clients-logo clients-logo--more">
                <span>And 25+ more…</span>
              </li>
            )}
          </ul>
          <p className="sr-only" aria-live="polite">
            Page {page} of {pages}
          </p>
        </div>
        <button
          ref={nextRef}
          type="button"
          className="clients-logos__arrow clients-logos__arrow--next"
          aria-label="Next clients"
          aria-controls="clients-logos-window"
          disabled={last}
          onClick={() => go(page + 1)}
          data-trapezium-id="packages.clients.logos-next"
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

export default function Clients() {
  return (
    <Screen id="packages.clients" title="Trusted by leading labs" className="clients">
      <div className="clients-head">
        <div>
          <p className="eyebrow clients-eyebrow">Packages · 2 of 2</p>
          <h1 className="headline clients-headline">
            Trusted by leading labs
            <br /> and hospitals, worldwide.
          </h1>
        </div>
        <p className="lede clients-lede">
          From independent diagnostic labs to large hospital networks, healthcare providers across the world
          use NirogGyan to deliver clearer, more engaging health reports and drive better patient outcomes.
        </p>
      </div>

      <ul className="card clients-stats" aria-label="NirogGyan in numbers">
        {STATS.map(({ key, Glyph, tone, value, label }) => (
          <li key={key} className="clients-stat">
            <span className={`clients-stat__icon clients-tone--${tone}`} aria-hidden="true">
              <Glyph size={34} />
            </span>
            <span>
              <strong className="clients-stat__value">{value}</strong>
              <span className="clients-stat__label">{label}</span>
            </span>
          </li>
        ))}
      </ul>

      <ul className="clients-quotes" aria-label="What our clients say">
        {TESTIMONIALS.map(({ key, Mark, Avatar, art, tone, quote, role, org }) => (
          <li key={key} className={`card clients-quote clients-quote--${key}`}>
            <TestimonialArt kind={art} />
            <figure className="clients-quote__body">
              <Mark />
              <blockquote className="clients-quote__text">
                <p>“{quote}”</p>
              </blockquote>
              <figcaption className="clients-quote__by">
                <span className={`clients-quote__avatar clients-tone--${tone}`} aria-hidden="true">
                  <Avatar size={26} />
                </span>
                <span>
                  <strong>{role}</strong>
                  <span>
                    {org[0]}
                    <br /> {org[1]}
                  </span>
                </span>
              </figcaption>
            </figure>
          </li>
        ))}
      </ul>

      <LogoCarousel />
    </Screen>
  );
}
